export const DTLS_STALL_MS = 4_000;

export type ConnectionStateVerdict = 'established' | 'failed' | null;

// Janus is the DTLS server for subscribers, so a handshake is 2 RTT; libwebrtc retransmits
// the ClientHello at 0.4/1.2/2.8 s. 4 s = four attempts each with ≥1 s to finish.
export class DtlsStallDetector {
  private established = false;

  private iceConnectedAt: number | null = null;

  private stallTimer?: number;

  private readonly onStall: () => void;

  constructor(onStall: () => void) {
    this.onStall = onStall;
  }

  get dtlsEverConnected() {
    return this.established;
  }

  get iceConnectedWithoutDtls() {
    return this.iceConnectedAt !== null && !this.established;
  }

  iceConnectMs(now = Date.now()) {
    return this.iceConnectedAt === null ? null : now - this.iceConnectedAt;
  }

  // Firefox < 113 never fires onconnectionstatechange, so arming the timer there would
  // force-rebuild every PC whose webrtcup lands >4s after ICE connect.
  onIceConnected(connectionStateSupported: boolean, now = Date.now()) {
    if (this.iceConnectedAt === null) {
      this.iceConnectedAt = now;
    }

    if (!connectionStateSupported || this.established || this.stallTimer !== undefined) {
      return;
    }

    this.stallTimer = window.setTimeout(() => {
      this.stallTimer = undefined;
      this.onStall();
    }, DTLS_STALL_MS);
  }

  onConnectionState(state: RTCPeerConnectionState): ConnectionStateVerdict {
    if (state === 'connected') {
      this.markEstablished();
      return 'established';
    }

    if (state === 'failed' && this.iceConnectedWithoutDtls) {
      return 'failed';
    }

    return null;
  }

  markEstablished() {
    this.established = true;
    this.dispose();
  }

  resetForNewPc() {
    this.dispose();
    this.established = false;
    this.iceConnectedAt = null;
  }

  dispose() {
    window.clearTimeout(this.stallTimer);
    this.stallTimer = undefined;
  }
}
